import { z } from 'zod';
import { FIELD_TYPES, PRIORITIES, ROLES } from './constants.js';

export const cuid = z.string().min(1);
export const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');
export const isoDateTime = z.string().datetime();

// ---- Auth ----
export const meSchema = z.object({
  id: cuid,
  email: z.string().email(),
  name: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  onboardedAt: z.string().nullable(),
  organizations: z.array(
    z.object({
      id: cuid,
      name: z.string(),
      slug: z.string(),
      role: z.enum(ROLES),
    }),
  ),
  currentOrgId: cuid.nullable(),
});
export type Me = z.infer<typeof meSchema>;

// ---- Stage ----
export const stageSchema = z.object({
  id: cuid,
  name: z.string().min(1).max(60),
  order: z.number().int().min(0),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .default('#6366f1'),
  isWon: z.boolean().default(false),
  isLost: z.boolean().default(false),
  probability: z.number().int().min(0).max(100).default(0),
  slaDays: z.number().int().min(1).nullable().optional(),
});
export const createStageSchema = stageSchema.omit({ id: true });
export const updateStageSchema = stageSchema.partial().omit({ id: true });
export type Stage = z.infer<typeof stageSchema>;

// ---- Lead group ----
// User-defined, Monday-style groups for organizing leads into subtables in the
// List view. Independent of Stage; a lead's groupId is set manually.
export const leadGroupSchema = z.object({
  id: cuid,
  name: z.string().min(1).max(80),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .default('#6366f1'),
  order: z.number().int().min(0).default(0),
});
export const createLeadGroupSchema = leadGroupSchema.omit({ id: true });
export const updateLeadGroupSchema = leadGroupSchema.partial().omit({ id: true });
export type LeadGroup = z.infer<typeof leadGroupSchema>;

// ---- Custom field ----
export const customFieldSchema = z.object({
  id: cuid,
  key: z
    .string()
    .min(1)
    .max(60)
    .regex(/^[a-z0-9_]+$/),
  label: z.string().min(1).max(120),
  type: z.enum(FIELD_TYPES),
  options: z.array(z.string()).default([]),
  showInList: z.boolean().default(true),
  order: z.number().int().min(0).default(0),
});
export const createCustomFieldSchema = customFieldSchema.omit({ id: true });
export const updateCustomFieldSchema = customFieldSchema.partial().omit({ id: true });
export type CustomField = z.infer<typeof customFieldSchema>;

// ---- Activity type ----
export const activityTypeSchema = z.object({
  id: cuid,
  name: z.string().min(1).max(60),
  order: z.number().int().min(0).default(0),
  template: z.string().max(2000).nullable().optional(),
});
export const createActivityTypeSchema = activityTypeSchema.omit({ id: true });
export type ActivityType = z.infer<typeof activityTypeSchema>;

// ---- Tag ----
export const tagSchema = z.object({
  id: cuid,
  name: z.string().min(1).max(60),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .default('#6366f1'),
});
export const createTagSchema = tagSchema.omit({ id: true });
export type Tag = z.infer<typeof tagSchema>;

// ---- Lead ----
const customFieldsValuesSchema = z
  .record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]))
  .default({});

export const leadSchema = z.object({
  id: cuid,
  company: z.string().min(1).max(200),
  contactName: z.string().max(200).nullable().optional(),
  contactTitle: z.string().max(200).nullable().optional(),
  email: z.string().email().nullable().optional().or(z.literal('')),
  phone: z.string().max(60).nullable().optional(),
  source: z.string().max(120).nullable().optional(),
  stageId: cuid,
  groupId: cuid.nullable().optional(),
  nreValue: z.number().nonnegative().nullable().optional(),
  dealValueYearly: z.number().nonnegative().nullable().optional(),
  priority: z.enum(PRIORITIES).default('B'),
  ownerUserId: cuid.nullable().optional(),
  nextAction: z.string().max(500).nullable().optional(),
  nextActionDate: isoDate.nullable().optional(),
  notes: z.string().max(20000).nullable().optional(),
  customFields: customFieldsValuesSchema,
  tagIds: z.array(cuid).default([]),
  position: z.number().default(0),
  stageEnteredAt: isoDateTime,
  wonReason: z.string().max(120).nullable().optional(),
  lostReason: z.string().max(120).nullable().optional(),
  createdAt: isoDateTime,
  lastActivityAt: isoDateTime.nullable(),
  deletedAt: isoDateTime.nullable(),
});
export type Lead = z.infer<typeof leadSchema>;

export const createLeadSchema = leadSchema
  .omit({
    id: true,
    createdAt: true,
    lastActivityAt: true,
    deletedAt: true,
    position: true,
    stageEnteredAt: true,
  })
  .extend({
    company: z.string().min(1).max(200),
  });
export type CreateLeadInput = z.infer<typeof createLeadSchema>;

export const updateLeadSchema = createLeadSchema.partial().extend({
  // Last Touch is normally auto-stamped, but can be set manually from the List view.
  lastActivityAt: isoDate.nullable().optional(),
});
export type UpdateLeadInput = z.infer<typeof updateLeadSchema>;

// ---- Activity ----
export const activitySchema = z.object({
  id: cuid,
  leadId: cuid,
  typeId: cuid.nullable(),
  typeName: z.string(),
  date: isoDate,
  summary: z.string().max(20000),
  loggedByUserId: cuid.nullable(),
  loggedByName: z.string().nullable(),
  createdAt: isoDateTime,
});
export type Activity = z.infer<typeof activitySchema>;

export const createActivitySchema = z.object({
  typeId: cuid.optional(),
  typeName: z.string().min(1).max(60).optional(),
  date: isoDate,
  summary: z.string().min(1).max(20000),
});
export type CreateActivityInput = z.infer<typeof createActivitySchema>;

// ---- Saved view ----
export const savedViewSchema = z.object({
  id: cuid,
  name: z.string().min(1).max(120),
  filters: z.object({
    search: z.string().optional(),
    stageId: cuid.optional(),
    priority: z.enum(PRIORITIES).optional(),
    ownerUserId: cuid.optional(),
    tagIds: z.array(cuid).optional(),
    overdueOnly: z.boolean().optional(),
    staleOnly: z.boolean().optional(),
  }),
  sortBy: z.string().nullable(),
  sortDir: z.enum(['asc', 'desc']).nullable(),
  isShared: z.boolean().default(false),
});
export const createSavedViewSchema = savedViewSchema.omit({ id: true });
export type SavedView = z.infer<typeof savedViewSchema>;

// ---- Org / user ----
export const orgSchema = z.object({
  id: cuid,
  name: z.string(),
  slug: z.string(),
  quarterlyGoalDeals: z.number().int().nullable().optional(),
  quarterlyGoalRevenue: z.number().nonnegative().nullable().optional(),
  wonReasons: z.array(z.string()).default([]),
  lostReasons: z.array(z.string()).default([]),
});
export type Org = z.infer<typeof orgSchema>;

export const updateOrgSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  quarterlyGoalDeals: z.number().int().min(0).nullable().optional(),
  quarterlyGoalRevenue: z.number().nonnegative().nullable().optional(),
  wonReasons: z.array(z.string().max(120)).optional(),
  lostReasons: z.array(z.string().max(120)).optional(),
});

export const memberSchema = z.object({
  id: cuid,
  userId: cuid,
  email: z.string().email(),
  name: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  role: z.enum(ROLES),
  joinedAt: isoDateTime,
});
export type Member = z.infer<typeof memberSchema>;

export const inviteSchema = z.object({
  id: cuid,
  email: z.string().email(),
  role: z.enum(ROLES).default('member'),
  createdAt: isoDateTime,
  expiresAt: isoDateTime,
});
export const createInviteSchema = z.object({
  email: z.string().email(),
  role: z.enum(ROLES).default('member'),
});
export type Invite = z.infer<typeof inviteSchema>;

// ---- Audit log ----
export const auditEntrySchema = z.object({
  id: cuid,
  leadId: cuid.nullable(),
  userId: cuid.nullable(),
  userName: z.string().nullable(),
  action: z.string(),
  fromValue: z.any().nullable(),
  toValue: z.any().nullable(),
  createdAt: isoDateTime,
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;

// ---- Attachment ----
export const attachmentSchema = z.object({
  id: cuid,
  leadId: cuid,
  fileName: z.string(),
  contentType: z.string(),
  size: z.number().int(),
  uploadedByName: z.string().nullable(),
  createdAt: isoDateTime,
  downloadUrl: z.string().url().optional(),
});
export type Attachment = z.infer<typeof attachmentSchema>;

// ---- Reports ----
// ---- Notifications / digest ----
export const notificationPrefsSchema = z.object({
  notifyDailyDigest: z.boolean(),
  notifyOnAssign: z.boolean(),
  notifyOnStageChange: z.boolean(),
});
export type NotificationPrefs = z.infer<typeof notificationPrefsSchema>;

export const pipelineReportSchema = z.object({
  byStage: z.array(
    z.object({
      stageId: cuid,
      stageName: z.string(),
      count: z.number(),
      nreSum: z.number(),
      yearlySum: z.number(),
      color: z.string(),
    }),
  ),
  totals: z.object({
    leads: z.number(),
    activeLeads: z.number(),
    wonLeads: z.number(),
    lostLeads: z.number(),
    nreSum: z.number(),
    yearlySum: z.number(),
    activeNre: z.number(),
    activeYearly: z.number(),
    wonNre: z.number(),
    wonYearly: z.number(),
    // Probability-weighted forecast across active leads.
    forecastYearly: z.number(),
    forecastNre: z.number(),
  }),
  lostReasons: z
    .array(z.object({ reason: z.string(), count: z.number(), yearly: z.number() }))
    .default([]),
  wonReasons: z
    .array(z.object({ reason: z.string(), count: z.number(), yearly: z.number() }))
    .default([]),
  bySource: z
    .array(
      z.object({
        source: z.string(),
        total: z.number(),
        won: z.number(),
        lost: z.number(),
        winRate: z.number(),
      }),
    )
    .default([]),
  goals: z
    .object({
      quarterStart: z.string(),
      quarterEnd: z.string(),
      goalDeals: z.number().nullable(),
      goalRevenue: z.number().nullable(),
      actualDeals: z.number(),
      actualRevenue: z.number(),
    })
    .nullable(),
  conversion: z.array(
    z.object({
      stageId: cuid,
      stageName: z.string(),
      enteredCount: z.number(),
      stillThereCount: z.number(),
      passedThroughCount: z.number(),
    }),
  ),
  velocity: z.object({
    avgDaysToWin: z.number().nullable(),
    avgDaysToLost: z.number().nullable(),
  }),
  byOwner: z.array(
    z.object({
      userId: cuid.nullable(),
      name: z.string(),
      activeCount: z.number(),
      wonCount: z.number(),
      pipelineYearly: z.number(),
    }),
  ),
});
export type PipelineReport = z.infer<typeof pipelineReportSchema>;
